import {
  ImageScaleView,
  MaskView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import { BuddiesBakerPet } from './BuddiesBakerPet';
import { BuddiesBakerProfile } from './BuddiesBakerProfile';
import { BuddiesBakerScore } from './BuddiesBakerScore';
import {
  buddiesEligibilityThreshold,
  buddiesMaxParty,
} from 'src/replicant/buddies/buddies.ruleset';
import {
  BuddiesEventStatus,
  buddiesGetMVP,
} from 'src/replicant/buddies/buddies.getters';
import { BuddiesPartyPlayer } from '../buddies.utils';
import { downscale } from 'src/conf/scale';
import { BuddiesBakerChest } from './BuddiesBakerChest';
import { BuddiesSparkles } from './BuddiesSparkles';
import { assertNever } from 'src/replicant/utils';
import { waitForItPromise } from 'src/lib/utils';

type BuddiesStallOpts = ViewOpts & {
  onInvite?: () => Promise<void>;
  onTapPlayer?: (player: BuddiesPartyPlayer) => void;
  onTapPet?: (player: BuddiesPartyPlayer) => void;
  onTapScore?: (player: BuddiesPartyPlayer) => void;
};

export class BuddiesStall extends View {
  private base: ImageScaleView;
  private mask: MaskView;
  private sparkles: BuddiesSparkles;
  private awning: ImageScaleView;
  private pets: BuddiesBakerPet[] = [];
  private profiles: BuddiesBakerProfile[] = [];
  private scores: BuddiesBakerScore[] = [];
  private chests: BuddiesBakerChest[] = [];
  private players: BuddiesPartyPlayer[] = [];

  constructor(opts?: BuddiesStallOpts) {
    super({
      ...opts,
      width: downscale(1050),
      height: downscale(1836),
      centerOnOrigin: true,
    });

    this.base = new ImageScaleView({
      superview: this,
      image: 'assets/ui/buddies/popupBaking/stall.png',
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height / 2,
    });

    this.mask = new MaskView({
      superview: this,
      mask: 'assets/ui/match2/common/solid_mask.png',
      width: this.style.width,
      height: 300,
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: 805,
    });

    this.sparkles = new BuddiesSparkles({
      superview: this.mask,
      width: 600,
      height: this.mask.style.height - 50,
      x: this.mask.style.width / 2 - 300,
      y: 0,
    });

    this.awning = new ImageScaleView({
      superview: this,
      image: 'assets/ui/buddies/popupBaking/stall_awning.png',
      width: downscale(1022),
      height: downscale(135),
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.mask.style.y - this.mask.style.height / 2,
    });

    const maskBottom = this.mask.style.y + this.mask.style.height / 2;

    for (let i = 0; i < buddiesMaxParty; i++) {
      const baker = new BuddiesBakerPet({
        superview: this.mask,
        x: 110 + i * 160,
        y: 250,
        zIndex: 1,
        onTapPet: () => opts?.onTapPet?.(this.players[i]),
      });
      this.pets.push(baker);

      const chest = new BuddiesBakerChest({
        superview: this.mask,
        x: baker.style.x,
        y: this.mask.style.height - 100,
        index: i,
        onInvite: opts?.onInvite,
      });
      this.chests.push(chest);

      const score = new BuddiesBakerScore({
        superview: this,
        x: baker.style.x,
        y: maskBottom + 45,
        onTap: () => opts?.onTapScore?.(this.players[i]),
      });
      this.scores.push(score);

      const profile = new BuddiesBakerProfile({
        superview: this,
        x: baker.style.x,
        y: maskBottom + 170,
        onInvite: opts?.onInvite,
        onTapPlayer: () => opts?.onTapPlayer?.(this.players[i]),
        index: i,
      });
      this.profiles.push(profile);
    }
  }

  public updatePlayers(players: BuddiesPartyPlayer[]) {
    this.players = players;
    const mvpId = buddiesGetMVP(players)?.id;

    for (let i = 0; i < buddiesMaxParty; i++) {
      const player = players[i];
      if (player) {
        this.pets[i].show();
        this.pets[i].setPet(player.species);
        const isMvp = player.id === mvpId;
        const isEligible = player.score >= buddiesEligibilityThreshold;
        this.pets[i].updateState({
          mvp: isMvp,
          eligible: isEligible,
          eventEnded: false,
        });
        this.scores[i].setScore(player.score, player.scoreAdded);
        this.profiles[i].setPlayer(player);
        this.chests[i].hide();
      } else {
        this.pets[i].clearPet();
        this.pets[i].hide();
        this.scores[i].clearScore();
        this.profiles[i].clearPlayer();
        this.chests[i].show();
      }
    }
  }

  public async animatePetsShowUp() {
    // Show all pets at once, catching errors to avoid unhandled rejections
    for (let i = 0; i < this.pets.length; i++) {
      if (!this.players[i]) continue;
      await this.pets[i].animateShowUp();
    }

    // Then animate all added scores in sequence (only for occupied slots)
    for (let i = 0; i < this.pets.length; i++) {
      if (!this.players[i]) continue;
      await waitForItPromise(500);
      this.scores[i].animateScore();
    }
  }

  public updateEventStatus(status: BuddiesEventStatus) {
    const mvpId = buddiesGetMVP(this.players)?.id;
    for (let i = 0; i < this.players.length; i++) {
      this.pets[i].updateState({
        mvp: this.players[i].id === mvpId,
        eligible: this.players[i].score >= buddiesEligibilityThreshold,
        eventEnded: status !== 'inProgress',
      });
    }

    switch (status) {
      case 'inProgress':
        this.eventInProgress();
        break;
      case 'overPendingScore':
      case 'overPendingCollect':
      case 'overClaimed':
      case 'overNoClaim':
      case 'unavailable':
        this.eventOver();
        break;
      default:
        assertNever(status);
    }
  }

  private eventInProgress() {
    this.profiles.forEach((profile) => profile.button.setDisabled(false));
  }

  private eventOver() {
    this.profiles.forEach((profile) => profile.button.setDisabled(true));
  }

  public getPlayerPosition(player: BuddiesPartyPlayer) {
    const playerIndex = this.players.findIndex((p) => p.id === player.id);
    if (playerIndex === -1) return null;
    const profile = this.profiles[playerIndex];
    return {
      x: profile.style.x,
      y: profile.style.y,
    };
  }
}
